import { action, makeObservable, observable, reaction } from 'mobx';
import {
  ALTERNATIVE_DICE_WIN_CHANCE_PRECISION,
  ALTERNATIVE_DICE_RANGE_TYPE,
  ALTERNATIVE_DICE_DEFAULT_RANGE_VALUE,
  ALTERNATIVE_DICE_RANGE_SIDE,
  ALTERNATIVE_DICE_MULTIPLIER_PRECISION,
  ALTERNATIVE_DICE_MULTIPLIER_LIMIT,
  ALTERNATIVE_DICE_MARKET_TYPE,
  ALTERNATIVE_DICE_MULTIPLIER_STEP_VALUE,
  ALTERNATIVE_DICE_WIN_CHANCE_STEP_VALUE,
  ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS,
  ALTERNATIVE_DICE_HIGH_LOW_LIMIT,
  ORIGINAL_DICE_MULTIPLIER_MIN,
  DEFAULT_MULTIPLIER_LIMITS,
} from '../alternativeDiceCore/constants';
import {
  parseNumberByPrecision,
  parseNumberPrecisionByDoteCount,
} from '../../../core/helpers/parseNumberByPrecision';
import { userInfoStore } from '../../../store/userInfoStore';
import {
  countValidRangeValueByOdd,
  getOdd,
  differenceBetweenSideByRtp,
} from '../alternativeDiceCore/helpers';
import { isMobile } from '../../../core/helpers/isMobile';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { AlternativeDiceSoundsName } from '../alternativeDiceCore/sounds';
import { freeBetStore } from '../../../store/freeBetStore';

class AlternativeDiceBetsStore {
  isBetsClosed = false;

  limits = ALTERNATIVE_DICE_MULTIPLIER_LIMIT;

  rangeValue = ALTERNATIVE_DICE_DEFAULT_RANGE_VALUE;

  rangeSide = null;

  rangeType = ALTERNATIVE_DICE_RANGE_TYPE.IN;

  multiplierLimit = DEFAULT_MULTIPLIER_LIMITS;

  multiplier = parseNumberByPrecision(
    getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp),
    1,
    ALTERNATIVE_DICE_MULTIPLIER_PRECISION
  );

  winChance = parseNumberByPrecision(
    100,
    userInfoStore.rtp / +this.multiplier,
    ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
  );

  payout = parseNumberByPrecision(
    this.multiplier,
    userInfoStore.betAmount,
    userInfoStore.precision
  );

  gameResult = null;

  lastResults = [];

  isWin = null;

  lastChangeInputType = null;

  isStartAnimation = false;

  winAmount = null;

  winOdd = null;

  differenceRangeSide =
    this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] -
    this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From];

  minimumRangeDifferenceByType = {
    [ALTERNATIVE_DICE_MARKET_TYPE.In]: differenceBetweenSideByRtp(
      userInfoStore.rtp,
      ALTERNATIVE_DICE_MARKET_TYPE.In,
      ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet
    ),
    [ALTERNATIVE_DICE_MARKET_TYPE.Out]: differenceBetweenSideByRtp(
      userInfoStore.rtp,
      ALTERNATIVE_DICE_MARKET_TYPE.Out,
      ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet
    ),
  };

  rangeMultipleMaxMin = {
    [ALTERNATIVE_DICE_MARKET_TYPE.In]: {
      maxBet: parseNumberByPrecision(
        getOdd(
          { [ALTERNATIVE_DICE_RANGE_SIDE.From]: 1, [ALTERNATIVE_DICE_RANGE_SIDE.To]: 1 },
          ALTERNATIVE_DICE_MARKET_TYPE.In,
          userInfoStore.rtp
        ),
        1,
        ALTERNATIVE_DICE_MULTIPLIER_PRECISION
      ),
      minBet: 1.01,
    },
    [ALTERNATIVE_DICE_MARKET_TYPE.Out]: {
      maxBet: parseNumberByPrecision(
        getOdd(
          { [ALTERNATIVE_DICE_RANGE_SIDE.From]: 0, [ALTERNATIVE_DICE_RANGE_SIDE.To]: 9998 },
          ALTERNATIVE_DICE_MARKET_TYPE.Out,
          userInfoStore.rtp
        ),
        1,
        ALTERNATIVE_DICE_MULTIPLIER_PRECISION
      ),
      minBet: 1.01,
    },
  };

  constructor() {
    makeObservable(this, {
      isWin: observable,
      winOdd: observable,
      payout: observable,
      rangeSide: observable,
      limits: observable,
      winChance: observable,
      winAmount: observable,
      rangeType: observable,
      rangeValue: observable,
      multiplier: observable,
      gameResult: observable,
      isBetsClosed: observable,
      multiplierLimit: observable,
      isStartAnimation: observable,
      lastChangeInputType: observable,
      differenceRangeSide: observable,
      lastResults: observable.deep,
      rangeMultipleMaxMin: observable.deep,
      // rangeCurrentValue: observable,
      setIsWin: action.bound,
      setWinOdd: action.bound,
      setRangeType: action.bound,
      setWinAmount: action.bound,
      setWinChance: action.bound,
      setRangeSide: action.bound,
      setRangeValue: action.bound,
      setGameResult: action.bound,
      setMultiplier: action.bound,
      setLimits: action.bound,
      checkValidOdd: action.bound,
      setLastResults: action.bound,
      updateRangeType: action.bound,
      setIsBetsClosed: action.bound,
      blurHighLowInput: action.bound,
      updateRangeValue: action.bound,
      updateLastResults: action.bound,
      setIsStartAnimation: action.bound,
      countOddByRangeValue: action.bound,
      detectRangeValueByOdd: action.bound,
      setRangeMultipleMaxMin: action.bound,
      setLastChangeInputType: action.bound,
      handleIncreaseWinChance: action.bound,
      handleIncreaseMultiplier: action.bound,
      updateDifferenceRangeSide: action.bound,
      setRangeValueWithMultipleSide: action.bound,
    });
    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => userInfoStore.betAmount,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.updatePayout(this.multiplier, value);
        }
      }
    );
    reaction(
      () => userInfoStore.margin,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.multiplierLimit = {
            minBet: 1.01,
            maxBet: 10000 * userInfoStore.rtp,
          };
          this.setRangeMultipleMaxMin(1 - userInfoStore.margin / 100);
          this.setMultiplier(
            parseNumberByPrecision(
              getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp),
              1,
              ALTERNATIVE_DICE_MULTIPLIER_PRECISION
            )
          );
        }
      }
    );
    reaction(
      () => this.multiplier,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.setGameResult(null);
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prev) => {
        if (value && value.minOdd && (!prev || prev.id !== value.id)) {
          this.setMultiplier(value.minOdd);
          this.setLimits({
            minBet: value.minOdd,
          });
        } else if (!value) {
          this.setLimits(ALTERNATIVE_DICE_MULTIPLIER_LIMIT);
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prev) => {
        if (value && value.minOdd && (!prev || prev.id !== value.id)) {
          this.setMultiplier(value.minOdd);
          this.countOddByRangeValue(this.rangeValue, true);
          this.setLimits({
            minBet: this.multiplier,
          });
        } else if (!value) {
          this.setMultiplier(DEFAULT_MULTIPLIER_LIMITS);
        }
      }
    );
  }

  setWinAmount(amount) {
    this.winAmount = amount;
  }

  setWinOdd(odd) {
    this.winOdd = odd;
  }

  setLimits(arg) {
    this.limits = {
      ...this.limits,
      ...arg,
    };
  }

  setRangeMultipleMaxMin(rtp) {
    this.rangeMultipleMaxMin = {
      [ALTERNATIVE_DICE_MARKET_TYPE.In]: {
        maxBet: parseNumberByPrecision(
          getOdd(
            { [ALTERNATIVE_DICE_RANGE_SIDE.To]: 1, [ALTERNATIVE_DICE_RANGE_SIDE.From]: 1 },
            ALTERNATIVE_DICE_MARKET_TYPE.In,
            rtp
          ),
          1
        ),
        minBet: 1.01,
      },
      [ALTERNATIVE_DICE_MARKET_TYPE.Out]: {
        maxBet: parseNumberByPrecision(
          getOdd(
            { [ALTERNATIVE_DICE_RANGE_SIDE.To]: 9998, [ALTERNATIVE_DICE_RANGE_SIDE.From]: 0 },
            ALTERNATIVE_DICE_MARKET_TYPE.Out,
            rtp
          ),
          1
        ),
        minBet: 1.01,
      },
    };
  }

  setIsStartAnimation(bool) {
    this.isStartAnimation = bool;
  }

  updateDifferenceRangeSide(rangeValue) {
    this.differenceRangeSide =
      rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] - rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From];
  }

  setRangeValueWithMultipleSide(rangeValue) {
    if (!this.isBetsClosed) {
      this.rangeValue = rangeValue;
      this.countOddByRangeValue();
      this.updateDifferenceRangeSide(this.rangeValue);
    }
  }

  setLastChangeInputType(type) {
    this.lastChangeInputType = type;
  }

  setRangeSide(side) {
    if (
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From] >
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To]
    ) {
      this.rangeSide = this.lastChangeInputType;
    }
    if (
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From] ===
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To]
    ) {
      this.rangeSide =
        +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From] ===
        ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min
          ? ALTERNATIVE_DICE_RANGE_SIDE.To
          : ALTERNATIVE_DICE_RANGE_SIDE.From;
    } else {
      this.rangeSide = side;
    }
  }

  setIsWin(bool) {
    this.isWin = bool;
  }

  setLastResults(lastResults) {
    this.lastResults = lastResults.map((item, index) => ({ ...item, id: index }));
  }

  updateLastResults(lastResult) {
    const updatedLastResult = [...this.lastResults, lastResult];
    if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
      updatedLastResult.splice(0, 1);
    }
    const lastEl = updatedLastResult.pop();
    this.setGameResult(lastResult.resultNumber);
    this.setLastResults(updatedLastResult);
    setTimeout(
      () => {
        this.setIsWin(lastResult.isWin);
        this.setLastResults([...updatedLastResult, lastEl]);
      },
      userSettingsStore.isTurboOn ? 0 : 600
    );
  }

  setGameResult(result) {
    if (result) {
      result = `0000${result}`;
    }
    this.gameResult = result;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setPayout(payout) {
    this.payout = parseNumberByPrecision(payout, 1, userInfoStore.precision);
  }

  updatePayout(multiplier, amount) {
    this.payout = parseNumberByPrecision(multiplier, amount, userInfoStore.precision);
  }

  setWinChance(winChance) {
    this.winChance = winChance;
    const newMultiplier = parseNumberPrecisionByDoteCount(
      100,
      userInfoStore.rtp / +winChance,
      ALTERNATIVE_DICE_MULTIPLIER_PRECISION
    );
    this.multiplier = Number.isFinite(newMultiplier) ? +newMultiplier : 0;
  }

  setMultiplier(odd) {
    this.multiplier = odd;
    this.setPayout(odd * userInfoStore.betAmount);
    const newWinChance = parseNumberPrecisionByDoteCount(
      100,
      userInfoStore.rtp / +odd,
      ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
    );
    this.winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
  }

  countRangeSideValue() {
    const middleValue = Math.ceil(
      (+this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] -
        +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From]) /
        2 +
        +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From]
    );
    const difference = differenceBetweenSideByRtp(
      userInfoStore.rtp,
      this.rangeType,
      this.multiplier
    );
    let rangeFromValue =
      this.rangeType === ALTERNATIVE_DICE_RANGE_TYPE.IN
        ? Math.ceil(middleValue - Math.ceil(Math.abs(difference / 2)))
        : Math.floor(middleValue - Math.abs(difference / 2));
    let rangeToValue =
      this.rangeType === ALTERNATIVE_DICE_RANGE_TYPE.IN
        ? Math.floor(middleValue + difference / 2)
        : Math.ceil(middleValue + difference / 2);
    let addedValue = 0;
    let toFrom = true;
    if (rangeFromValue < ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min) {
      addedValue = Math.abs(rangeFromValue);
      rangeFromValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min;
    }
    if (rangeToValue > ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max) {
      if (
        this.rangeType === ALTERNATIVE_DICE_RANGE_TYPE.OUT &&
        rangeFromValue === ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min
      ) {
        rangeToValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max - 1;
      } else {
        addedValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max - rangeToValue;
        toFrom = false;
        rangeToValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max;
      }
    }
    return { rangeFromValue, rangeToValue, toFrom, addedValue };
  }

  detectRangeValueByOdd() {
    const { rangeFromValue, rangeToValue, toFrom, addedValue } = this.countRangeSideValue();
    const rangeValue = {
      [ALTERNATIVE_DICE_RANGE_SIDE.To]: rangeToValue + (toFrom ? addedValue : 0),
      [ALTERNATIVE_DICE_RANGE_SIDE.From]: rangeFromValue - (toFrom ? 0 : addedValue),
    };
    this.setRangeValueWithMultipleSide(rangeValue);
  }

  checkValidOdd() {
    if (this.multiplier < ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet && this.multiplier !== null) {
      const rangeSideValue = countValidRangeValueByOdd({
        odd: ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet,
        rangeValue: this.rangeValue,
        rtp: userInfoStore.rtp,
        rangeType: this.rangeType,
        isMultiple: this.rangeSide === null,
      });
      if (this.rangeSide === null) {
        this.setRangeValueWithMultipleSide(rangeSideValue);
      } else {
        this.setRangeValue(Math.ceil(rangeSideValue[+this.rangeSide]));
      }
    } else if (this.multiplier === null) {
      const rangeValue = {
        ...this.rangeValue,
        [this.rangeSide]:
          this.rangeValue[this.rangeSide] +
          (+this.rangeSide === ALTERNATIVE_DICE_RANGE_SIDE.To ? -1 : 1),
      };
      this.setRangeValueWithMultipleSide(rangeValue);
    }
  }

  countRangeValueBySide() {
    const multiplier = getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp);
    if (multiplier < ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet) {
      const minDifference = differenceBetweenSideByRtp(
        userInfoStore.rtp,
        this.rangeType,
        ORIGINAL_DICE_MULTIPLIER_MIN
      );
      const middleValue =
        Math.round(
          (this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] -
            this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From]) /
            2
        ) + this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From];
      let addedValueSideTo = 0;
      let addedValueSideFrom = 0;
      let rangeFromValue = middleValue - Math.floor(minDifference / 2);
      let rangeToValue = middleValue + Math.ceil(minDifference / 2);
      if (rangeFromValue < ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min) {
        addedValueSideTo += Math.abs(rangeFromValue);
        rangeFromValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.min;
      }
      if (rangeToValue > ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max) {
        addedValueSideFrom += Math.abs(ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max) - rangeToValue;
        rangeToValue = ALTERNATIVE_DICE_RANGE_LIMIT_OPTIONS.max;
      }
      this.rangeValue = {
        [ALTERNATIVE_DICE_RANGE_SIDE.To]: rangeToValue + addedValueSideTo,
        [ALTERNATIVE_DICE_RANGE_SIDE.From]: rangeFromValue + addedValueSideFrom,
      };
      this.countOddByRangeValue();
    } else {
      this.setMultiplier(
        parseNumberByPrecision(multiplier, 1, ALTERNATIVE_DICE_MULTIPLIER_PRECISION)
      );
    }
  }

  setRangeType(rangeType) {
    if (rangeType === this.rangeType) return;
    soundService.play(AlternativeDiceSoundsName.toggle, true);
    this.rangeType = rangeType;
    this.countRangeValueBySide();
  }

  updateRangeType() {
    const rangeType =
      this.rangeType === ALTERNATIVE_DICE_RANGE_TYPE.OUT
        ? ALTERNATIVE_DICE_RANGE_TYPE.IN
        : ALTERNATIVE_DICE_RANGE_TYPE.OUT;
    this.setRangeType(rangeType);
  }

  setRangeValue(value) {
    if (!this.isBetsClosed) {
      const isValidRangeValue = this.detectIsValidRangeValue(this.rangeType, value);
      if (isValidRangeValue) {
        if (value !== this.rangeValue[0] && value !== this.rangeValue[1]) {
          if (userSettingsStore.isSoundOn) {
            soundService.play(AlternativeDiceSoundsName.toggle, true);
          }
        }
        this.rangeValue = {
          ...this.rangeValue,
          [this.rangeSide]: +value,
        };
        this.updateDifferenceRangeSide(this.rangeValue);
      }
      this.countOddByRangeValue();
    }
    // soundService.play(DiceSoundsName.toggle);
  }

  detectIsValidRangeValue(rangeType, value) {
    if (
      +this.rangeSide === ALTERNATIVE_DICE_RANGE_SIDE.From &&
      this.rangeSide !== null &&
      value > this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To]
    ) {
      return false;
    }
    return !(
      +this.rangeSide === ALTERNATIVE_DICE_RANGE_SIDE.To &&
      value < this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From]
    );
  }

  updateRangeValue(value, type) {
    this.rangeValue = {
      ...this.rangeValue,
      [type]: +value,
    };
    this.updateDifferenceRangeSide(this.rangeValue);
    this.countOddByRangeValue();
  }

  updateRangeValueWithHotKeys(isIncrease) {
    const currentSide = isIncrease
      ? ALTERNATIVE_DICE_RANGE_SIDE.To
      : ALTERNATIVE_DICE_RANGE_SIDE.From;
    let nextValue = this.rangeValue[currentSide] + (isIncrease ? 1 : -1);
    if (currentSide === ALTERNATIVE_DICE_RANGE_SIDE.From) {
      if (nextValue < ALTERNATIVE_DICE_HIGH_LOW_LIMIT.minBet) {
        soundService.play(AlternativeDiceSoundsName.toggle, true);
        nextValue = ALTERNATIVE_DICE_HIGH_LOW_LIMIT.minBet;
      } else {
        soundService.play(AlternativeDiceSoundsName.toggle, true);
      }
    } else if (nextValue > ALTERNATIVE_DICE_HIGH_LOW_LIMIT.maxBet) {
      nextValue = ALTERNATIVE_DICE_HIGH_LOW_LIMIT.maxBet;
    } else {
      soundService.play(AlternativeDiceSoundsName.toggle, true);
    }
    const nextRangeValue = {
      ...this.rangeValue,
      [currentSide]: nextValue,
    };
    const nextOdd = getOdd(nextRangeValue, this.rangeType, userInfoStore.rtp);
    if (
      nextOdd > this.rangeMultipleMaxMin[this.rangeType].minBet &&
      nextOdd < this.rangeMultipleMaxMin[this.rangeType].maxBet
    ) {
      this.setRangeValueWithMultipleSide(nextRangeValue);
    }
  }

  countOddByRangeValue() {
    const multiplier = getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp);
    this.multiplier = parseNumberByPrecision(1, multiplier, ALTERNATIVE_DICE_MULTIPLIER_PRECISION);
    this.winChance = parseNumberByPrecision(
      100,
      userInfoStore.rtp / this.multiplier,
      ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
    );
    this.updatePayout(this.multiplier, userInfoStore.betAmount);
  }

  handleIncreaseMultiplier(isIncrease) {
    let nextMultiplierValue = parseNumberByPrecision(
      +this.multiplier + (isIncrease ? 1 : -1) * ALTERNATIVE_DICE_MULTIPLIER_STEP_VALUE,
      1,
      ALTERNATIVE_DICE_MULTIPLIER_PRECISION
    );

    if (+this.multiplier === this.rangeMultipleMaxMin[this.rangeType].maxBet) {
      if (!isIncrease) {
        nextMultiplierValue = +this.multiplier / 2;
      } else {
        return null;
      }
    }
    if (nextMultiplierValue > this.rangeMultipleMaxMin[this.rangeType].maxBet) {
      this.setMultiplier(this.rangeMultipleMaxMin[this.rangeType].maxBet);
    } else if (nextMultiplierValue < this.rangeMultipleMaxMin[this.rangeType].minBet) {
      this.setMultiplier(this.rangeMultipleMaxMin[this.rangeType].minBet);
    } else {
      this.setMultiplier(nextMultiplierValue);
    }
    return this.detectRangeValueByOdd();
  }

  handleIncreaseWinChance(isIncrease) {
    const multipleMaxMin = this.rangeMultipleMaxMin[this.rangeType];
    const winChanceMaxMin = {
      maxBet: parseNumberPrecisionByDoteCount(
        100,
        userInfoStore.rtp / multipleMaxMin.minBet,
        ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
      ),
      minBet: parseNumberPrecisionByDoteCount(
        100,
        userInfoStore.rtp / multipleMaxMin.maxBet,
        ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
      ),
    };
    const nextWinChanceValue =
      +this.winChance + (isIncrease ? 1 : -1) * ALTERNATIVE_DICE_WIN_CHANCE_STEP_VALUE;
    if (nextWinChanceValue > winChanceMaxMin.maxBet) {
      this.setWinChance(winChanceMaxMin.maxBet);
    } else if (nextWinChanceValue < winChanceMaxMin.minBet) {
      this.setWinChance(winChanceMaxMin.minBet);
    } else {
      this.setWinChance(
        parseNumberByPrecision(1, nextWinChanceValue, ALTERNATIVE_DICE_WIN_CHANCE_PRECISION)
      );
    }
    this.detectRangeValueByOdd();
  }

  blurHighLowInput(type) {
    if (ALTERNATIVE_DICE_HIGH_LOW_LIMIT.maxBet <= +this.rangeValue[type]) {
      this.updateRangeValue(ALTERNATIVE_DICE_HIGH_LOW_LIMIT.maxBet, type);
    }
    if (ALTERNATIVE_DICE_HIGH_LOW_LIMIT.minBet >= +this.rangeValue[type]) {
      this.updateRangeValue(ALTERNATIVE_DICE_HIGH_LOW_LIMIT.minBet, type);
    }
    if (
      type === ALTERNATIVE_DICE_RANGE_SIDE.To &&
      +this.rangeValue[type] < +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From]
    ) {
      this.updateRangeValue(
        +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From],
        ALTERNATIVE_DICE_RANGE_SIDE.To
      );
    }
    if (
      type === ALTERNATIVE_DICE_RANGE_SIDE.From &&
      +this.rangeValue[type] > +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To]
    ) {
      this.updateRangeValue(
        +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To],
        ALTERNATIVE_DICE_RANGE_SIDE.From
      );
    }
    const odd = getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp);
    if (odd < ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet) {
      if (type === ALTERNATIVE_DICE_RANGE_SIDE.From) {
        this.updateRangeValue(
          this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] -
            this.minimumRangeDifferenceByType[this.rangeType],
          type
        );
      } else {
        this.updateRangeValue(
          +this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From] +
            this.minimumRangeDifferenceByType[this.rangeType],
          type
        );
      }
    }
  }

  resetState() {
    this.isBetsClosed = false;
    this.rangeValue = ALTERNATIVE_DICE_DEFAULT_RANGE_VALUE;
    this.rangeSide = null;
    this.rangeType = ALTERNATIVE_DICE_RANGE_TYPE.IN;
    this.multiplierLimit = {
      minBet: 1.01,
      maxBet: 10000 * userInfoStore.rtp,
    };
    this.multiplier = parseNumberByPrecision(
      getOdd(this.rangeValue, this.rangeType, userInfoStore.rtp),
      1,
      ALTERNATIVE_DICE_MULTIPLIER_PRECISION
    );
    this.winChance = parseNumberByPrecision(
      100,
      userInfoStore.rtp / +this.multiplier,
      ALTERNATIVE_DICE_WIN_CHANCE_PRECISION
    );
    this.payout = parseNumberByPrecision(
      this.multiplier,
      userInfoStore.betAmount,
      userInfoStore.precision
    );
    this.gameResult = null;
    this.lastResults = [];
    this.isWin = null;
    this.lastChangeInputType = null;
    this.isStartAnimation = false;
    this.winAmount = null;
    this.winOdd = null;
    this.differenceRangeSide =
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.To] -
      this.rangeValue[ALTERNATIVE_DICE_RANGE_SIDE.From];
    this.minimumRangeDifferenceByType = {
      [ALTERNATIVE_DICE_MARKET_TYPE.In]: differenceBetweenSideByRtp(
        userInfoStore.rtp,
        ALTERNATIVE_DICE_MARKET_TYPE.In,
        ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet
      ),
      [ALTERNATIVE_DICE_MARKET_TYPE.Out]: differenceBetweenSideByRtp(
        userInfoStore.rtp,
        ALTERNATIVE_DICE_MARKET_TYPE.Out,
        ALTERNATIVE_DICE_MULTIPLIER_LIMIT.minBet
      ),
    };
    this.rangeMultipleMaxMin = {
      [ALTERNATIVE_DICE_MARKET_TYPE.In]: {
        maxBet: parseNumberByPrecision(
          getOdd(
            { [ALTERNATIVE_DICE_RANGE_SIDE.From]: 1, [ALTERNATIVE_DICE_RANGE_SIDE.To]: 1 },
            ALTERNATIVE_DICE_MARKET_TYPE.In,
            userInfoStore.rtp
          ),
          1,
          ALTERNATIVE_DICE_MULTIPLIER_PRECISION
        ),
        minBet: 1.01,
      },
      [ALTERNATIVE_DICE_MARKET_TYPE.Out]: {
        maxBet: parseNumberByPrecision(
          getOdd(
            { [ALTERNATIVE_DICE_RANGE_SIDE.From]: 0, [ALTERNATIVE_DICE_RANGE_SIDE.To]: 9998 },
            ALTERNATIVE_DICE_MARKET_TYPE.Out,
            userInfoStore.rtp
          ),
          1,
          ALTERNATIVE_DICE_MULTIPLIER_PRECISION
        ),
        minBet: 1.01,
      },
    };
  }
}

export const alternativeDiceBetsStore = new AlternativeDiceBetsStore();
